import {
    ChatCircleText,
    EnvelopeSimple,
    LinkSimple,
    WhatsappLogo,
} from "phosphor-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import useLanguage from "../../hooks/language";
import {
    buildMailtoUrl,
    buildShareMessage,
    buildShareSheetData,
    buildShareUrl,
    buildSmsUrl,
    buildWhatsAppUrl,
    copyLink,
    detectMobilePlatform,
    SHARE_EMAIL_SUBJECT,
    ShareChannel,
    ShareOutcome,
    shareOrCopyLink,
} from "../../utils/share";
import styles from "./share-block.module.css";

const ILLUSTRATION_SRC =
    "https://userimg-assets-eu.customeriomail.com/images/client-env-167668/01KXR66R92R7KCJNY0PVGDDAHJ.png";

const FEEDBACK_DURATION_MS = 2000;
const ICON_SIZE = 26;

type ShareBlockProps = {
    /** Channel to highlight, e.g. the email button the user came from. */
    emphasizedChannel?: ShareChannel;
};

const ShareBlock: React.FunctionComponent<ShareBlockProps> = ({
    emphasizedChannel,
}) => {
    const { translations } = useLanguage();
    const t = translations.shareBlock;

    const platform = useMemo(
        () =>
            detectMobilePlatform(navigator.userAgent, navigator.maxTouchPoints),
        []
    );

    const linkUrl = buildShareUrl("direct_link");
    const smsMessage = buildShareMessage(buildShareUrl("sms"));
    const whatsAppMessage = buildShareMessage(buildShareUrl("whatsapp"));
    const emailMessage = buildShareMessage(buildShareUrl("email"));

    // State

    const [status, setStatus] = useState("");
    const [showSmsNote, setShowSmsNote] = useState(false);
    const linkInputRef = useRef<HTMLInputElement>(null);
    const feedbackTimeout = useRef<number>();

    useEffect(() => () => window.clearTimeout(feedbackTimeout.current), []);

    // Handlers

    const showFeedback = (message: string) => {
        window.clearTimeout(feedbackTimeout.current);
        setStatus(message);
        feedbackTimeout.current = window.setTimeout(
            () => setStatus(""),
            FEEDBACK_DURATION_MS
        );
    };

    const selectLink = () => {
        linkInputRef.current?.focus();
        linkInputRef.current?.select();
    };

    const handleOutcome = (outcome: ShareOutcome) => {
        if (outcome === "copied") showFeedback(t.linkCopied);
        if (outcome === "manual") {
            selectLink();
            showFeedback(t.manualCopy);
        }
    };

    const onCopyClick = async () => {
        handleOutcome(
            await shareOrCopyLink(buildShareSheetData(linkUrl), navigator)
        );
    };

    const onDesktopSmsClick = async () => {
        setShowSmsNote(true);
        handleOutcome(await copyLink(linkUrl, navigator));
    };

    // Rendering

    const buttonClass = (channel: ShareChannel) =>
        `${styles.button} ${
            emphasizedChannel === channel ? styles.buttonEmphasized : ""
        }`;

    return (
        <section className={styles.card} aria-labelledby="share-block-title">
            <div className={styles.top}>
                <div>
                    <span className={styles.pill}>{t.pill}</span>
                    <h2 id="share-block-title" className={styles.title}>
                        {t.title}
                    </h2>
                </div>
                <img
                    className={styles.illustration}
                    src={ILLUSTRATION_SRC}
                    alt=""
                />
            </div>

            <p className={styles.description}>{t.description}</p>

            <div className={styles.buttons}>
                {platform ? (
                    <a
                        className={buttonClass("sms")}
                        href={buildSmsUrl(platform, smsMessage)}
                        aria-label={t.smsLabel}
                    >
                        <ChatCircleText size={ICON_SIZE} aria-hidden />
                    </a>
                ) : (
                    <button
                        type="button"
                        className={buttonClass("sms")}
                        onClick={onDesktopSmsClick}
                        aria-label={t.smsLabel}
                    >
                        <ChatCircleText size={ICON_SIZE} aria-hidden />
                    </button>
                )}
                <a
                    className={buttonClass("whatsapp")}
                    href={buildWhatsAppUrl(whatsAppMessage)}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={t.whatsAppLabel}
                >
                    <WhatsappLogo size={ICON_SIZE} aria-hidden />
                </a>
                <a
                    className={buttonClass("email")}
                    href={buildMailtoUrl(SHARE_EMAIL_SUBJECT, emailMessage)}
                    aria-label={t.emailLabel}
                >
                    <EnvelopeSimple size={ICON_SIZE} aria-hidden />
                </a>
                <button
                    type="button"
                    className={buttonClass("copy")}
                    onClick={onCopyClick}
                    aria-label={t.copyLabel}
                >
                    <LinkSimple size={ICON_SIZE} aria-hidden />
                </button>
            </div>

            {showSmsNote && <p className={styles.note}>{t.smsDesktopNote}</p>}

            <label className={styles.linkLabel} htmlFor="share-block-link">
                {t.linkLabel}
            </label>
            <input
                id="share-block-link"
                ref={linkInputRef}
                className={styles.link}
                type="text"
                readOnly
                value={linkUrl}
                onFocus={(e) => e.currentTarget.select()}
            />

            <p className={styles.status} role="status" aria-live="polite">
                {status}
            </p>
        </section>
    );
};

export default ShareBlock;
